import React, { useState, useMemo } from 'react';
import { BarChart3, User, MapPin, FileText, TrendingUp } from 'lucide-react';
import CustomBarChart from 'components/common/CustomBarChart';

const ClassificacaoOSSDashboard = ({ data }) => {
  const [classificacaoAtiva, setClassificacaoAtiva] = useState('por_tecnico');

  const agregarPorCampo = (lista = [], chavesCampo = [], chavesTotal = ['TOTAL']) => {
    const agrupados = {};

    lista.forEach((item) => {
      const nomeBruto = chavesCampo
        .map((chave) => item?.[chave])
        .find((valor) => valor !== undefined && valor !== null && String(valor).trim() !== '');
      const nome = String(nomeBruto || 'N/A').trim() || 'N/A';

      const totalBruto = chavesTotal
        .map((chave) => item?.[chave])
        .find((valor) => valor !== undefined && valor !== null && String(valor).trim() !== '');
      const total = totalBruto !== undefined ? parseInt(totalBruto || 0, 10) : 1;
      const valorSeguro = Number.isFinite(total) ? total : 0;

      agrupados[nome] = (agrupados[nome] || 0) + valorSeguro;
    });

    return Object.entries(agrupados)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  };

  // Opções de classificação (sem tempo)
  const opcoesClassificacao = [
    { id: 'por_diagnostico', label: 'Por Diagnóstico', icon: FileText, cor: 'green' },
    { id: 'por_assunto', label: 'Por Assunto', icon: TrendingUp, cor: 'purple' },
    { id: 'por_tecnico', label: 'Por Técnico', icon: User, cor: 'indigo' },
    { id: 'por_cidade', label: 'Por Cidade', icon: MapPin, cor: 'red' },
    { id: 'por_bairro', label: 'Por Bairro', icon: MapPin, cor: 'yellow' }
  ];

  // Processar dados baseado na classificação ativa
  const dadosProcessados = useMemo(() => {
    // Extrair dados dentro do useMemo para evitar dependências externas
    const porDiagnostico = data.por_diagnostico?.oss || [];
    const porAssunto = data.por_assunto?.oss || [];
    const porTecnico = data.por_tecnico?.oss || [];
    const porCidade = data.por_cidade?.oss || [];
    const porBairro = data.por_bairro?.oss || [];

    let dados = [];
    let titulo = '';
    let cor = 'var(--accent)';

    switch (classificacaoAtiva) {
      case 'por_diagnostico':
        // Agregar TODOS os registros por diagnóstico
        const detalhamento = data.detalhamento?.oss || [];
        const agrupados = {};
        
        // Processar por_diagnostico se existir e tiver dados
        if (porDiagnostico.length > 0) {
          porDiagnostico.forEach(item => {
            // Normalizar campo Diagnóstico (pode vir como DIAGNOSTICO, Diagnostico, ou diagnostico)
            const diagnostico = item.DIAGNOSTICO || item.Diagnostico || item.diagnostico || 'N/A';
            
            // Se tem TOTAL, usar o valor; senão, contar como 1
            const total = item.TOTAL !== undefined ? parseInt(item.TOTAL || 0) : 1;
            
            if (!agrupados[diagnostico]) {
              agrupados[diagnostico] = 0;
            }
            
            agrupados[diagnostico] += total;
          });
        }
        
        // Fallback: se não tem por_diagnostico ou está vazio, usar detalhamento
        if (Object.keys(agrupados).length === 0 && detalhamento.length > 0) {
          detalhamento.forEach(item => {
            const diagnostico = item.DIAGNOSTICO || item.Diagnostico || item.diagnostico || 'N/A';
            
            if (!agrupados[diagnostico]) {
              agrupados[diagnostico] = 0;
            }
            
            agrupados[diagnostico] += 1;
          });
        }
        
        // Converter para array, ordenar por total (decrescente) e pegar os 15 primeiros
        dados = Object.entries(agrupados)
          .map(([diagnostico, total]) => ({
            name: diagnostico.substring(0, 40) + (diagnostico.length > 40 ? '...' : ''),
            value: total
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 15);
        
        titulo = 'OSS por Diagnóstico';
        cor = 'var(--status-good)';
        break;

      case 'por_assunto':
        dados = agregarPorCampo(
          porAssunto,
          ['ASSUNTO', 'Assunto', 'assunto'],
          ['TOTAL', 'total']
        )
          .map((item) => ({
            name: item.name.substring(0, 40) + (item.name.length > 40 ? '...' : ''),
            value: item.value
          }))
          .slice(0, 15);
        titulo = 'OSS por Assunto';
        cor = 'var(--accent)';
        break;

      case 'por_tecnico':
        // Agregar TODOS os registros por técnico
        const detalhamentoTecnico = data.detalhamento?.oss || [];
        const agrupadosTecnico = {};
        
        // Processar por_tecnico se existir e tiver dados
        if (porTecnico.length > 0) {
          porTecnico.forEach(item => {
            // Normalizar campo Técnico (pode vir como TÉCNICO, Técnico, ou tecnico)
            const tecnico = item['TÉCNICO'] || item['Técnico'] || item['tecnico'] || 'N/A';
            
            // Se tem TOTAL, usar o valor; senão, contar como 1
            const total = item.TOTAL !== undefined ? parseInt(item.TOTAL || 0) : 1;
            
            if (!agrupadosTecnico[tecnico]) {
              agrupadosTecnico[tecnico] = 0;
            }
            
            agrupadosTecnico[tecnico] += total;
          });
        }
        
        // Fallback: se não tem por_tecnico ou está vazio, usar detalhamento
        if (Object.keys(agrupadosTecnico).length === 0 && detalhamentoTecnico.length > 0) {
          detalhamentoTecnico.forEach(item => {
            const tecnico = item['TÉCNICO'] || item['Técnico'] || item['tecnico'] || 'N/A';
            
            if (!agrupadosTecnico[tecnico]) {
              agrupadosTecnico[tecnico] = 0;
            }
            
            agrupadosTecnico[tecnico] += 1;
          });
        }
        
        // Converter para array, ordenar por total (decrescente) e pegar os 15 primeiros
        dados = Object.entries(agrupadosTecnico)
          .map(([tecnico, total]) => ({
            name: (String(tecnico).substring(0, 30)) + (String(tecnico).length > 30 ? '...' : ''),
            value: total
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 15);
        
        titulo = 'OSS por Técnico';
        cor = 'var(--accent)';
        break;

      case 'por_cidade':
        // Agregar TODOS os registros por cidade
        const detalhamentoCidade = data.detalhamento?.oss || [];
        const agrupadosCidade = {};
        
        // Processar por_cidade se existir e tiver dados
        if (porCidade.length > 0) {
          porCidade.forEach(item => {
            // Normalizar campo Cidade (pode vir como CIDADE, Cidade, ou cidade)
            const cidade = item.CIDADE || item.Cidade || item.cidade || 'N/A';
            
            // Se tem TOTAL, usar o valor; senão, contar como 1
            const total = item.TOTAL !== undefined ? parseInt(item.TOTAL || 0) : 1;
            
            if (!agrupadosCidade[cidade]) {
              agrupadosCidade[cidade] = 0;
            }
            
            agrupadosCidade[cidade] += total;
          });
        }
        
        // Fallback: se não tem por_cidade ou está vazio, usar detalhamento
        if (Object.keys(agrupadosCidade).length === 0 && detalhamentoCidade.length > 0) {
          detalhamentoCidade.forEach(item => {
            const cidade = item.CIDADE || item.Cidade || item.cidade || 'N/A';
            
            if (!agrupadosCidade[cidade]) {
              agrupadosCidade[cidade] = 0;
            }
            
            agrupadosCidade[cidade] += 1;
          });
        }
        
        // Converter para array, ordenar por total (decrescente)
        dados = Object.entries(agrupadosCidade)
          .map(([cidade, total]) => ({
            name: cidade,
            value: total
          }))
          .sort((a, b) => b.value - a.value);
        
        titulo = 'OSS por Cidade';
        cor = 'var(--status-bad)';
        break;

      case 'por_bairro':
        // Agregar TODOS os registros por bairro
        const detalhamentoBairro = data.detalhamento?.oss || [];
        const agrupadosBairro = {};
        
        // Processar por_bairro se existir e tiver dados
        if (porBairro.length > 0) {
          porBairro.forEach(item => {
            // Normalizar campo Bairro (pode vir como BAIRRO, Bairro, ou bairro)
            const bairro = item.BAIRRO || item.Bairro || item.bairro || 'N/A';
            
            // Se tem TOTAL, usar o valor; senão, contar como 1
            const total = item.TOTAL !== undefined ? parseInt(item.TOTAL || 0) : 1;
            
            if (!agrupadosBairro[bairro]) {
              agrupadosBairro[bairro] = 0;
            }
            
            agrupadosBairro[bairro] += total;
          });
        }
        
        // Fallback: se não tem por_bairro ou está vazio, usar detalhamento
        if (Object.keys(agrupadosBairro).length === 0 && detalhamentoBairro.length > 0) {
          detalhamentoBairro.forEach(item => {
            const bairro = item.BAIRRO || item.Bairro || item.bairro || 'N/A';
            
            if (!agrupadosBairro[bairro]) {
              agrupadosBairro[bairro] = 0;
            }
            
            agrupadosBairro[bairro] += 1;
          });
        }
        
        // Converter para array, ordenar por total (decrescente) e pegar os 15 primeiros
        dados = Object.entries(agrupadosBairro)
          .map(([bairro, total]) => ({
            name: bairro,
            value: total
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 15);
        
        titulo = 'OSS por Bairro';
        cor = 'var(--status-warn)';
        break;

      default:
        dados = [];
        titulo = 'Selecione uma classificação';
    }

    return { dados, titulo, cor };
  }, [classificacaoAtiva, data]);

  return (
    <div className="space-y-6">
      {/* Seletor de Classificação */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2 text-[color:var(--accent)]" />
          Classificação de OSS
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {opcoesClassificacao.map((opcao) => {
            const Icon = opcao.icon;
            const isActive = classificacaoAtiva === opcao.id;

            return (
              <button
                key={opcao.id}
                onClick={() => setClassificacaoAtiva(opcao.id)}
                className={`p-3 rounded-lg border transition-all duration-200 ${
                  isActive
                    ? 'border-[color:var(--accent)] bg-[color:var(--accent)] text-white'
                    : 'border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-[color:var(--accent)]'}`} />
                  <span className="text-xs font-medium text-center">{opcao.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Gráfico da Classificação Selecionada */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)]">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4">
            {dadosProcessados.titulo}
            <span className="ml-2 text-sm text-[color:var(--text-muted)]">({dadosProcessados.dados.length} registros)</span>
          </h3>

          {dadosProcessados.dados.length > 0 ? (
            <CustomBarChart
              data={dadosProcessados.dados}
              title=""
              dataKey="value"
              nameKey="name"
              color={dadosProcessados.cor}
              height={400}
              sortBy="value"
              sortOrder="desc"
            />
          ) : (
            <div className="text-center py-12 text-[color:var(--text-muted)]">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-[color:var(--text-muted)]" />
              <p>Nenhum dado disponível para esta classificação</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassificacaoOSSDashboard;



